import { LelySpinner } from '@/common/components';
import { ColumnDef, flexRender, Row } from '@tanstack/react-table';
import { notUndefined, Virtualizer } from '@tanstack/react-virtual';
import {
  getLastLeftPinnedColumnStyles,
  getPinnedColumnStyles,
} from './TanTable';
import clsx from 'clsx';

type Props = {
  rowVirtualizer: Virtualizer<HTMLDivElement, Element>;
  rows: Row<any>[];
  columns: ColumnDef<any, any>[];
  noDataMessage: string;
  isScrolledRight?: boolean;
  isLoading?: boolean;
  isEmpty?: boolean;
  enableRowHover?: boolean;
  onRowClick?: (row: any) => void;
  onRowSelection?: (row: Row<any>) => void;
};
function TanTableBody({
  rowVirtualizer,
  rows,
  columns,
  noDataMessage,
  isScrolledRight,
  isLoading,
  isEmpty,
  enableRowHover = true,
  onRowClick,
  onRowSelection,
}: Props) {
  const items = rowVirtualizer.getVirtualItems();

  // Prevents the sticky header from disappearing when using scroll and virtualization
  const [before, after] =
    items.length > 0 && !isLoading
      ? [
          notUndefined(items[0]).start - rowVirtualizer.options.scrollMargin,
          rowVirtualizer.getTotalSize() -
            notUndefined(items[items.length - 1]).end,
        ]
      : [0, 0];

  const handleOnClick = (row: Row<any>) => {
    const hasTextSelection = document.getSelection()?.toString();

    if (!hasTextSelection && onRowClick) {
      onRowClick(row.original);
    }

    if (onRowSelection) {
      onRowSelection(row);
    }
  };

  const noDataRow = (
    <tr>
      <td
        colSpan={columns.length}
        align="center"
        className="d-none d-md-table-cell no-data-cell"
      >
        {noDataMessage}
      </td>
    </tr>
  );

  const loadingSpinnerRow = (
    <tr>
      <td
        colSpan={columns.length}
        align="center"
        className="d-none d-md-table-cell loading-cell"
      >
        <LelySpinner label="Loading data..." />
      </td>
    </tr>
  );

  return (
    <tbody>
      {!isLoading && before > 0 && (
        <tr>
          <td colSpan={columns.length} style={{ height: before }} />
        </tr>
      )}

      {isLoading
        ? loadingSpinnerRow
        : items.map((virtualRow) => {
            const row = rows[virtualRow.index];
            return (
              <tr
                key={row.id}
                style={{
                  height: `${virtualRow.size}px`,
                }}
                className={clsx(
                  enableRowHover && 'hoverable-row',
                  row.getIsSelected() && 'selected-row',
                )}
                onClick={() => handleOnClick(row)}
              >
                {row.getVisibleCells().map((cell) => {
                  const meta = cell.column.columnDef.meta as
                    | {
                        testId?: string;
                        className?: string;
                        cellClassName?: (context: any) => string;
                      }
                    | undefined;
                  const customClassName =
                    typeof meta?.cellClassName === 'function'
                      ? meta.cellClassName(cell.getContext())
                      : meta?.className ?? '';
                  const testId =
                    (cell.column.columnDef as any).testId ||
                    (cell.column.columnDef.meta as any)?.testId;
                  return (
                    <td
                      key={cell.id}
                      data-testid={testId}
                      className={customClassName || undefined}
                      style={{
                        width:
                          cell.column.getSize() > 0
                            ? `${cell.column.getSize()}px`
                            : 'auto',
                        ...getPinnedColumnStyles(cell.column),
                        // ...getRightPinnedColumnStyles(cell.column),
                        ...(isScrolledRight
                          ? getLastLeftPinnedColumnStyles(cell.column)
                          : {}),
                      }}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </td>
                  );
                })}
              </tr>
            );
          })}

      {!isLoading && after > 0 && (
        <tr>
          <td colSpan={columns.length} style={{ height: after }} />
        </tr>
      )}

      {!isLoading && isEmpty && noDataRow}
    </tbody>
  );
}

export default TanTableBody;
